import { ELEMENT_CODES, ELEMENT_GROUPS } from "@/lib/elements";
import { DEFAULT_MISSING_POWER_SCORE, DEFAULT_POWER_SCORE_LENS } from "@/lib/score-source";
import {
  DEFAULT_CANDIDATE_ASSESSMENT_DETAIL,
  DEFAULT_CANDIDATE_ASSESSMENT_OUTCOME_FOCUS,
  DEFAULT_CANDIDATE_ASSESSMENT_STRICTNESS,
  DEFAULT_REQUIREMENT_LENIENCY,
} from "./design-codec";
import type {
  MatchAccess,
  MatchData,
  Person,
  PersonNodeLike,
  ProfileDetailField,
  StepId,
} from "./match-design-types";
import type { PowerGroupKey } from "./match-result-codec";

/**
 * Ceiling for the team-size stepper. genesis types `teamSize` as an untyped Object inside the match
 * design JSONB and validates nothing, and spark's own codec requires an INTEGER — so an unbounded or
 * fractional value is a value spark cannot read back. 999 is far past any real team while still
 * bounding what reaches the solver.
 */
export const TEAM_SIZE_MAX = 999;

export const STEPS: ReadonlyArray<{ n: string; id: StepId; label: string }> = [
  { n: "01", id: "challenge", label: "Challenge" },
  { n: "02", id: "people", label: "Teamplayer" },
  { n: "03", id: "result", label: "Results" },
];

// 0 = power excluded from the solver objective. The Power Profile Editor
// renders 0-valued tiles editable (not greyed out) so the user can dial them
// back up. Zeros round-trip end-to-end: preset authors use 0 to mean "this
// power doesn't matter", and the wire mapper persists them verbatim.
// Die Wire-Skala nutzt ganzzahlige Skalenpunkte: genesis speichert 0..1000 in
// team_profile_element.weight (V0048), die Oberflaeche teilt fuer die Anzeige
// durch 100 und zeigt zwei Nachkommastellen. Aus 7,39 auf dem Schirm wird 739
// auf der Leitung. Ganzzahlig geblieben, damit Spalte, GraphQL-Int und der
// Solver-Kontrakt unangetastet bleiben.
export const POWERS_WEIGHT_MIN = 0;
export const POWERS_WEIGHT_MAX = 1000;
export const POWERS_WEIGHT_DEFAULT = 500;
/** Teiler zwischen gespeichertem Wert und angezeigter Zahl (739 -> 7,39). */
export const POWERS_WEIGHT_DISPLAY_DIVISOR = 100;
export const POWERS_WEIGHT_DISPLAY_DECIMALS = 2;

// Every CreativityElement key spark knows about, in deterministic order. Used
// to densify elementWeights at the read/write boundary so consumers (silhouette
// + tile editor) can rely on the map being complete.
export const ALL_ELEMENT_KEYS: ReadonlyArray<string> = Object.freeze(
  Object.values(ELEMENT_GROUPS).flatMap((g) => g.elements),
);

// Fill any element key missing from `weights` with `missingDefault`. The
// caller picks the default per scenario: 0 when the profile carries a preset
// id (preset author intent — explicit zeros AND silently-omitted keys both
// mean "doesn't matter"), POWERS_WEIGHT_DEFAULT for the Individuell synthetic
// (baseline = 5 across the board).
export function densifyElementWeights(
  weights: Readonly<Record<string, number>> | undefined,
  missingDefault: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const el of ALL_ELEMENT_KEYS) {
    const v = weights?.[el];
    out[el] = typeof v === "number" ? v : missingDefault;
  }
  return out;
}

// Synthetic team profile representing the "Individuell" baseline – the user
// is on no preset, weights are equal across all elements unless they edit.
// Genesis requires a non-null team profile on the solver path
// (MatchResultGenerateController throws "teamProfile is required" otherwise),
// but only consumes `elementWeights` – id/name are stored in JSONB and never
// dereferenced. New matches default to this object; the wire layer also
// emits it when nothing is stored.
export const DEFAULT_TEAM_PROFILE_ID = "00000000-0000-0000-0000-000000000000";
export const DEFAULT_TEAM_PROFILE_NAME = "Standard (gleichgewichtet)";
export const DEFAULT_TEAM_PROFILE_ELEMENT_WEIGHTS: Readonly<Record<string, number>> =
  Object.freeze(
    Object.values(ELEMENT_GROUPS)
      .flatMap((g) => g.elements)
      .reduce<Record<string, number>>((acc, el) => {
        acc[el] = POWERS_WEIGHT_DEFAULT;
        return acc;
      }, {}),
  );

// Discrete four-level weighting UI over the stored 0..1000 scale (spark #173).
// Solver weights only matter relative to each other, so the UI offers four
// levels instead of a free numeric scale. Write values keep MEDIUM exactly at
// POWERS_WEIGHT_DEFAULT: applying "Mittel" on top of the Ausgewogen preset
// must not register as drift. Display bucketing maps arbitrary stored values
// (server presets can carry any 0..1000) onto the closest level; callers must
// treat re-selecting the current level as a no-op so a bucketed preset value
// (e.g. 739 shown as Mittel) is not silently normalised to the write value.
export type PowerWeightLevel = 0 | 1 | 2 | 3;
export const POWER_WEIGHT_LEVELS: readonly [number, number, number, number] = [
  0,
  300,
  POWERS_WEIGHT_DEFAULT,
  POWERS_WEIGHT_MAX,
];
export const POWER_WEIGHT_LEVEL_MESSAGES = [
  { id: "match.weightLevel.none", defaultMessage: "Nicht relevant" },
  { id: "match.weightLevel.low", defaultMessage: "Niedrig" },
  { id: "match.weightLevel.medium", defaultMessage: "Mittel" },
  { id: "match.weightLevel.high", defaultMessage: "Hoch" },
] as const;

export function powerWeightLevel(value: number): PowerWeightLevel {
  if (value <= 0) return 0;
  if (value <= 300) return 1;
  if (value <= 700) return 2;
  return 3;
}

// "Ausgewogen" (every element at the baseline 5) was a client-side synthetic
// preset until genesis#211 seeded it as a real TeamProfile row, so the picker
// no longer synthesizes it. Both ids stay: the synthetic one only ever lived
// in the Match design JSONB (genesis stores it and never dereferences it), so
// designs saved before the migration still carry it and are normalized onto
// the seeded row on read.
export const LEGACY_BALANCED_TEAM_PROFILE_ID = "00000000-0000-0000-0000-000000000001";
export const BALANCED_TEAM_PROFILE_SEED_ID = "5e3d6539-d6c2-4cdb-99ce-90244a394f3a";

export function makeIndividualTeamProfile(
  elementWeights?: Record<string, number>,
) {
  return {
    id: DEFAULT_TEAM_PROFILE_ID,
    name: DEFAULT_TEAM_PROFILE_NAME,
    elementWeights: { ...(elementWeights ?? DEFAULT_TEAM_PROFILE_ELEMENT_WEIGHTS) },
  };
}

export const EMPTY_MATCH_DATA: MatchData = {
  matchTitle: "",
  matchDescription: "",
  projectName: "",
  projectShortDescription: "",
  projectDescription: "",
  projectFiles: [],
  matchAccess: "private",
  promptHints: "",
  candidateAssessmentPrompt: null,
  candidateAssessmentDetail: DEFAULT_CANDIDATE_ASSESSMENT_DETAIL,
  candidateAssessmentStrictness: DEFAULT_CANDIDATE_ASSESSMENT_STRICTNESS,
  candidateAssessmentOutcomeFocus: DEFAULT_CANDIDATE_ASSESSMENT_OUTCOME_FOCUS,
  requirementLeniency: DEFAULT_REQUIREMENT_LENIENCY,
  // Die Defaults von genesis, damit ein Design ohne die Schluessel unveraendert weiterrechnet.
  powerScoreLens: DEFAULT_POWER_SCORE_LENS,
  missingPowerScore: DEFAULT_MISSING_POWER_SCORE,
  images: [],
  owner: "",
  timeframe: "",
  teamSize: 8,
  teamProfile: makeIndividualTeamProfile(),
  phase: "Development & Greenlight",
  kpis: null,
  done: false,
  agenticAi: false,
  agents: [],
  matchMode: null,
  conditions: [],
  unknownConditions: [],
  // null, nicht []: ein frisches Match hat keinen Unternehmens-Arm, bis der Unternehmens-Editor einen schreibt
  // (siehe MatchData.companyConditions und design-codec MatchDesign.companyConditions).
  companyConditions: null,
  unknownCompanyConditions: [],
  // Product decision, 2026-10-31 (listing review 44231): new matches start with AI enabled.
  // Explicit NO_AI on saved requirements is still respected by deriveAiEnabled.
  aiEnabled: true,
  requirements: {
    basics: { items: [] },
    summary: { items: [] },
    details: { items: [] },
    educations: { items: [] },
    certifications: { items: [] },
    expertise: { items: [] },
    languages: { items: [] },
    careers: { items: [] },
  },
};

export const MATCH_ACCESS_OPTIONS: ReadonlyArray<{
  value: MatchAccess;
  label: string;
  hint: string;
}> = [
  { value: "private", label: "Nur ich", hint: "Niemand außer dir sieht dieses Match." },
  { value: "team", label: "Team", hint: "Dein Team kann das Match sehen." },
  { value: "org", label: "Organisation", hint: "Alle in deiner Organisation." },
];

export const POWER_GROUPS: ReadonlyArray<{ key: PowerGroupKey; abbrs: string[] }> = (
  ["RESILIENCE", "RESULT_ORIENTATION", "INVENTIVENESS", "FUTURE_ORIENTATION", "TEAM_SPIRIT", "COMMUNICATION"] as const
).map((key) => ({
  key,
  /* v8 ignore next -- unreachable: the six keys above are exactly the keys of ELEMENT_GROUPS, so the
     optional chain and its ?? [] fallback can never be taken */
  abbrs: (ELEMENT_GROUPS[key as keyof typeof ELEMENT_GROUPS]?.elements ?? [])
    .map((el) => ELEMENT_CODES[el])
    .filter((s): s is string => Boolean(s)) as string[],
}));

// Maps a PowerGroupKey to its i18n key in `match.group.*`.
// Labels live in the message bundle so the constant stays a pure data shape.
export const powerGroupLabelId = (key: PowerGroupKey): string => `match.group.${key}`;

export const PROFILE_DETAIL_FIELDS: ProfileDetailField[] = [
  { key: "gender", label: "Geschlecht", type: "gender" },
  { key: "birthday", label: "Geburtstag", type: "date" },
  { key: "nationality", label: "Nationalität", type: "text" },
  { key: "maritalStatus", label: "Familienstand", type: "marital" },
  { key: "childCount", label: "Anzahl Kinder", type: "number" },
  { key: "website", label: "Website", type: "text" },
  { key: "profession", label: "Beruf", type: "text" },
  { key: "currentActivity", label: "Aktuelle Tätigkeit", type: "text" },
  { key: "currentEmployer", label: "Arbeitgeber", type: "text" },
  { key: "street", label: "Straße", type: "text" },
  { key: "houseNumber", label: "Hausnummer", type: "text" },
  { key: "apartment", label: "Wohnung", type: "text" },
  { key: "zipCode", label: "PLZ", type: "text" },
  { key: "drivingLicence", label: "Führerschein", type: "text" },
  {
    key: "currentTasks",
    label: "Aktuelle Themen / Projekte / Herausforderungen",
    type: "text",
  },
  { key: "miscellaneous", label: "Sonstiges", type: "text" },
  { key: "citizenship", label: "Staatsangehörigkeit", type: "country" },
];

export function isChallengeReady(data: MatchData): boolean {
  return (
    data.promptHints.trim().length > 0 ||
    data.projectDescription.trim().length > 0
  );
}

// Zeilen duerfen client-seitig kurz unvollstaendig existieren (leere Auswahl waehrend des
// Editierens), aber nicht zum Backend: genesis wirft auf leere UUIDs (z.B. Branche ohne
// Auswahl -> NPE in industryExperienceYears). Zaehlt aktive Conditions ohne Pflicht-Auswahl.
export function incompleteConditionCount(data: MatchData): number {
  const persons = data.conditions.filter((c) => {
    if (!c.active) return false;
    if (c.__typename === "IndustryExperienceCondition") {
      return !(c as { industryId?: string }).industryId;
    }
    if (c.__typename === "EducationCondition" || c.__typename === "CertificateCondition") {
      return !(c as { value?: string }).value?.trim();
    }
    if ("id" in c) return !(c as { id?: string }).id;
    return false;
  }).length;
  // Der Zaehler ist mode-agnostisch, weil companyConditions bei jedem Personen-Match null ist — an
  // Personen-Matches aendert der Unternehmens-Anteil also nichts.
  return persons + incompleteCompanyConditionCount(data);
}

/**
 * Halbfertige FIRMEN-Kriterien, gezaehlt fuer sich.
 *
 * Eigene Funktion, weil der Unternehmens-Lauf nur diese Haelfte blockieren darf: die Personen-Conditions
 * eines RANK_COMPANIES-Designs werden nie ausgewertet, und ein Lauf, den eine nie gelesene Zeile
 * aufhaelt, waere nicht erklaerbar. {@link incompleteConditionCount} zaehlt dieselbe Regel mit,
 * damit es nur EINE Definition von "halbfertig" je Kriterienart gibt.
 *
 * Beide heutigen Faelle sind dieselbe Klasse — eine Zeile, deren Pflichtangabe noch fehlt — auch
 * wenn sie unterschiedlich schaden:
 *
 * - Branche ohne Auswahl traegt industryId "", und genesis liest das Feld als UUID.
 * - Fachkompetenz ohne Formulierung traegt expertise "" (der Startwert einer frischen Zeile), und das
 *   ist keine Schreibfehler-Falle, sondern eine leere FRAGE: identische Formulierungen zaehlen
 *   automatisch, alles andere beurteilt die AI — ein Lauf wuerde das Modell also fragen, ob ein
 *   Unternehmen „" kann. Nur-Leerzeichen zaehlt genauso: genesis trimmt beim Vergleich nicht, und eine
 *   Anforderung aus Leerzeichen benennt so wenig wie eine leere.
 *
 * Anders als die Branchen-Zeile wird die Fachkompetenz-Zeile beim Speichern NICHT verworfen (siehe
 * dataToDesign): dort ist das Verwerfen unvermeidbar, weil ein "" als UUID den ganzen Save
 * abbricht, hier gibt es keinen solchen Zwang — und eine Zeile still zu loeschen, die eine Nutzerin
 * gerade angelegt hat (mitsamt Muss/Kann und Mindeststufe), waere ein Datenverlust statt einer
 * Sicherung.
 */
export function incompleteCompanyConditionCount(data: MatchData): number {
  return (data.companyConditions ?? []).filter((c) => {
    if (!c.active) return false;
    if (c.__typename === "CompanyIndustryCondition") return !c.industryId;
    if (c.__typename === "CompanyExpertiseCondition") return c.expertise.trim().length === 0;
    return false;
  }).length;
}

export function canPublishMatch(data: MatchData): boolean {
  return data.projectName.trim().length > 0;
}

export function nextStepId(current: StepId): StepId | null {
  const idx = STEPS.findIndex((s) => s.id === current);
  const nxt = STEPS[idx + 1];
  return nxt ? nxt.id : null;
}

export function prevStepId(current: StepId): StepId | null {
  const idx = STEPS.findIndex((s) => s.id === current);
  const prv = STEPS[idx - 1];
  return prv ? prv.id : null;
}

export function stepIndex(current: StepId): number {
  return STEPS.findIndex((s) => s.id === current);
}

export function parseStepId(value: string | null): StepId {
  return STEPS.some((s) => s.id === value) ? (value as StepId) : "challenge";
}

export function normalisePeople(list: unknown): Person[] {
  if (!Array.isArray(list)) return [];
  return list.map((p) =>
    typeof p === "string" ? ({ id: p, name: p, source: "Manuell" } as Person) : (p as Person),
  );
}

export function personFromNode(node: PersonNodeLike, source: string): Person {
  const givenRaw = node.name?.given;
  const given = Array.isArray(givenRaw) ? givenRaw : [givenRaw];
  const name =
    [...given, node.name?.family].filter(Boolean).join(" ") || node.formattedName || "?";
  return {
    id: node.id,
    name,
    source,
    role: node.jobTitle || node.profession || null,
  };
}

// Klemmt einen gelesenen Wert auf die Skala. Frueher hat diese Funktion Werte
// oberhalb des Maximums als "stammt wohl aus der 0..100-Skala" gedeutet und
// heruntergerechnet. Auf der 0..1000-Wire-Skala (V0048) geht dieses Raten nicht
// mehr: 50 ist dort ein voellig legitimer Wert (0,50 in der Anzeige), waere aber
// unter der alten Heuristik zu 5,00 aufgeblasen worden. Die Migration hat die
// relationalen Bestandsdaten umgerechnet; MatchDesign V1 wird in Genesis beim
// Lesen auf V2 gehoben. Damit entfaellt der Anlass fuer die Client-Heuristik.
export function normalizePowersWeight(raw: unknown): number {
  // `|| 0` faengt NaN bereits ab, ein Finite-Check danach kann nie greifen.
  const n = Math.round(Number(raw) || 0);
  return Math.min(POWERS_WEIGHT_MAX, Math.max(POWERS_WEIGHT_MIN, n));
}
